import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Route, Sunrise, Sunset } from 'lucide-react';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { CompanySettingsService } from '../../../api/services/CompanySettingsService';
import { CompanyWorkScheduleService } from '../../../api/services/CompanyWorkScheduleService';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  saveTravelTimeRules,
  type FirstAppointmentScheduling,
  type LastAppointmentScheduling,
} from '../../../business/onboarding/onboardingLocations';
import { formatTimeString } from '../../../utils/timeUtils';
import { OptionCard } from '../shared/OptionCard';
import { StepIntro } from '../shared/StepShell';

export const TravelTimeStep: React.FC = () => {
  const { completeStep } = useOnboarding();

  const [isLoading, setIsLoading] = useState(true);
  const [firstRule, setFirstRule] = useState<FirstAppointmentScheduling>('includes_travel_time');
  const [lastRule, setLastRule] = useState<LastAppointmentScheduling>('includes_return_travel');
  const [earliestStart, setEarliestStart] = useState('08:00');
  const [latestEnd, setLatestEnd] = useState('17:00');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [settings, schedule] = await Promise.all([
        CompanySettingsService.getApiAppCompanySettingsGet().catch(() => null),
        CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule().catch(
          () => null
        ),
      ]);
      if (cancelled) return;

      const prefs = settings?.routePreferences;
      if (prefs?.firstAppointmentScheduling === 'starts_at_work_hours') {
        setFirstRule('starts_at_work_hours');
      }
      if (prefs?.lastAppointmentScheduling === 'ends_at_work_hours') {
        setLastRule('ends_at_work_hours');
      }

      // Quote the hours just set on the previous step so each option reads as a
      // concrete statement about this user's day rather than an abstraction.
      const workDays = (schedule?.dailySchedules ?? []).filter(d => d.isWorkDay);
      const starts = workDays.map(d => d.startTime?.substring(0, 5)).filter(Boolean) as string[];
      const ends = workDays.map(d => d.endTime?.substring(0, 5)).filter(Boolean) as string[];
      if (starts.length) setEarliestStart(starts.sort()[0]);
      if (ends.length) setLatestEnd(ends.sort().reverse()[0]);

      setIsLoading(false);
    };

    load().catch(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useStepValidity(true);

  useStepSaver(
    ONBOARDING_STEP.TRAVEL,
    useCallback(async () => {
      await saveTravelTimeRules({
        firstAppointmentScheduling: firstRule,
        lastAppointmentScheduling: lastRule,
      });
      completeStep(ONBOARDING_STEP.TRAVEL);
    }, [firstRule, lastRule, completeStep])
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <StepIntro>
        Whether driving happens inside your working hours or on either side of them. This decides
        how early the first appointment can start and how late the last one can run.
      </StepIntro>

      <div>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-ink-700">
          <Sunrise className="h-3.5 w-3.5 text-ink-400" />
          At the start of the day
        </p>
        <div
          className="grid gap-2.5 sm:grid-cols-2"
          role="radiogroup"
          aria-label="First appointment rule"
        >
          <OptionCard
            selected={firstRule === 'includes_travel_time'}
            onSelect={() => setFirstRule('includes_travel_time')}
            icon={Route}
            title="Drive on my own time"
            badge="Common"
            description={`You leave before ${formatTimeString(earliestStart)} so the first appointment can start right at ${formatTimeString(earliestStart)}.`}
          />
          <OptionCard
            selected={firstRule === 'starts_at_work_hours'}
            onSelect={() => setFirstRule('starts_at_work_hours')}
            icon={Clock}
            title="Drive on the clock"
            description={`You leave at ${formatTimeString(earliestStart)}, so the first appointment starts once you've arrived.`}
          />
        </div>
      </div>

      <div>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-ink-700">
          <Sunset className="h-3.5 w-3.5 text-ink-400" />
          At the end of the day
        </p>
        <div
          className="grid gap-2.5 sm:grid-cols-2"
          role="radiogroup"
          aria-label="Last appointment rule"
        >
          <OptionCard
            selected={lastRule === 'includes_return_travel'}
            onSelect={() => setLastRule('includes_return_travel')}
            icon={Route}
            title="Drive home on my own time"
            badge="Common"
            description={`The last appointment can run until ${formatTimeString(latestEnd)}, then you head home.`}
          />
          <OptionCard
            selected={lastRule === 'ends_at_work_hours'}
            onSelect={() => setLastRule('ends_at_work_hours')}
            icon={Clock}
            title="Be home by close"
            description={`The last appointment finishes early enough to be back by ${formatTimeString(latestEnd)}.`}
          />
        </div>
      </div>
    </div>
  );
};
